import { getWindow } from '@r-client/shared/util/core';

import { log } from '../log';
import { E_OSANO_CONSENT_STATUS } from '../types';
import {
  IOsanoCm,
  IOsanoConsent,
  IOsanoWindow,
  TOsanoConsentEvent,
} from './osano-types';

/**
 * Osano may not have loaded by the time we subscribe, so we poll for it rather
 * than listening for `osano-cm-initialized` — that event fires once and we can
 * easily attach after it.
 *
 * Fast at first, then slowly until unsubscribed — never giving up, because a
 * late Osano can still carry a refusal.
 */
const FAST_POLL_INTERVAL_MS = 250;
const FAST_POLL_WINDOW_MS = 15_000;
const SLOW_POLL_INTERVAL_MS = 5_000;

const CONSENT_EVENTS: readonly TOsanoConsentEvent[] = [
  'osano-cm-consent-saved',
  'osano-cm-consent-changed',
];

export type TAnalyticsConsentListener = (isGranted: boolean) => void;

/** `undefined` when the map has no ANALYTICS entry: no answer, not a refusal. */
const toAnalyticsGranted = (
  consent: IOsanoConsent | undefined
): boolean | undefined =>
  consent?.ANALYTICS === undefined
    ? undefined
    : consent.ANALYTICS === E_OSANO_CONSENT_STATUS.ACCEPT;

/**
 * Reads Osano's ANALYTICS decision right now, without waiting.
 *
 * `undefined` means "no answer available yet" — Osano has not loaded, was
 * blocked, or is skipped entirely (mobile webview). That is distinct from a
 * recorded `DENY`, and callers must not collapse the two.
 *
 * Usually `undefined` at `initRum` time, which then falls back to the saved
 * copy. Kept so that loading Osano earlier would take effect unchanged.
 */
export const readAnalyticsConsent = (): boolean | undefined => {
  const globalWindow = getWindow<IOsanoWindow & Window>();

  try {
    return toAnalyticsGranted(globalWindow?.Osano?.cm?.getConsent?.());
  } catch (error) {
    log('osano getConsent failed %o', error);
    return undefined;
  }
};

/**
 * Calls `onChange` whenever Osano's ANALYTICS consent becomes known or changes.
 *
 * If Osano never loads (webview, ad blocker), `onChange` is simply never
 * called — an expected outcome, not an error.
 *
 * The returned unsubscribe is always safe to call, including before Osano
 * appeared.
 */
export const subscribeToAnalyticsConsent = (
  onChange: TAnalyticsConsentListener
): (() => void) => {
  const globalWindow = getWindow<IOsanoWindow & Window>();

  if (!globalWindow) {
    return () => undefined;
  }

  let isDisposed = false;
  let detach: (() => void) | undefined;

  const handleConsent: (consent: IOsanoConsent) => void = (consent) => {
    if (isDisposed) {
      return;
    }
    log('osano consent %o', consent);

    const isGranted = toAnalyticsGranted(consent);
    if (isGranted !== undefined) {
      onChange(isGranted);
    }
  };

  const attach = (cm: IOsanoCm | undefined): boolean => {
    if (!cm) {
      return false;
    }

    try {
      const initialConsent = cm.getConsent?.();
      if (initialConsent) {
        handleConsent(initialConsent);
      }
    } catch (error) {
      log('osano getConsent failed %o', error);
    }

    const attachedEvents: TOsanoConsentEvent[] = [];

    CONSENT_EVENTS.forEach((eventName) => {
      try {
        cm.addEventListener?.(eventName, handleConsent);
        attachedEvents.push(eventName);
      } catch (error) {
        // `osano-cm-consent-saved` is verified on staging;
        // `osano-cm-consent-changed` is documented but not yet observed. If
        // either name is rejected, it must degrade, not throw and take the
        // other listener down with it.
        log('osano addEventListener(%s) failed %o', eventName, error);
      }
    });

    detach = () => {
      attachedEvents.forEach((eventName) => {
        try {
          cm.removeEventListener?.(eventName, handleConsent);
        } catch (error) {
          log('osano removeEventListener(%s) failed %o', eventName, error);
        }
      });
    };

    return true;
  };

  if (attach(globalWindow.Osano?.cm)) {
    return () => {
      isDisposed = true;
      detach?.();
    };
  }

  const startedAt = Date.now();
  let isSlowPolling = false;
  let timeoutId: number | undefined;

  const poll = () => {
    if (isDisposed || attach(globalWindow.Osano?.cm)) {
      return;
    }

    if (!isSlowPolling && Date.now() - startedAt >= FAST_POLL_WINDOW_MS) {
      isSlowPolling = true;
      log(
        'osano not loaded within %dms — checking every %dms',
        FAST_POLL_WINDOW_MS,
        SLOW_POLL_INTERVAL_MS
      );
    }

    timeoutId = globalWindow.setTimeout(
      poll,
      isSlowPolling ? SLOW_POLL_INTERVAL_MS : FAST_POLL_INTERVAL_MS
    );
  };

  timeoutId = globalWindow.setTimeout(poll, FAST_POLL_INTERVAL_MS);

  return () => {
    isDisposed = true;
    globalWindow.clearTimeout(timeoutId);
    detach?.();
  };
};
